import { setIsLoading } from "features/company/slices/companySlice";
import useOptions from "features/masterData/hooks/useOptions";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import * as service from "../services/adminApi";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";

function toParams(params = {}) {
    const modifiedParams = {
        ...params,
        subDistrictId: params.subdistrictId,
        colorCode: ["#1D76DB", "#1D76DB", "#1D76DB", "#1D76DB"],
    };
    return modifiedParams;
}

const useAdminCreateCompany = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isLoading = useSelector((state) => state.company.isLoading);
    const [infoValues, setInfoValues] = useState({});
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const [optionSearchTax, setOptionSearchTax] = useState([]);
    const [addressValues, setAddressValues] = useState({});
    const [contactValues, setContactValues] = useState({});
    const [currentStep, setCurrentStep] = useState(0);

    const {
        companyTypeOptions = [],
        sourceProvinceOptions = [],
        sourceDistrictOptions = [],
        sourceSubDistrictOptions = [],
        getMstCompanyTypeOptions = () => { },
        getProvinceOptions,
        getDistrictOptions,
        getSubDistrictOptions,
        clearDistrictOptions,
        clearSubDistrictOptions,
    } = useOptions();

    const provinceOptionsTh = useMemo(() => {
        const modifiedOptions = sourceProvinceOptions.map((opt) => ({
            ...opt,
            label: opt.provinceName,
            value: opt.provinceId,
        })).sort((a, b) => a.label.localeCompare(b.label));
        return modifiedOptions;
    }, [sourceProvinceOptions]);

    const provinceOptionsEn = useMemo(() => {
        const modifiedOptions = sourceProvinceOptions.map((opt) => ({
            ...opt,
            label: opt.provinceNameEn,
            value: opt.provinceId,
        })).sort((a, b) => a.label.localeCompare(b.label));
        return modifiedOptions;
    }, [sourceProvinceOptions]);

    const districtOptionsTh = useMemo(() => {
        const modifiedOptions = sourceDistrictOptions.map((opt) => ({
            ...opt,
            label: opt.districtName,
            value: opt.districtId,
        })).sort((a, b) => a.label.localeCompare(b.label));
        return modifiedOptions;
    }, [sourceDistrictOptions]);

    const districtOptionsEn = useMemo(() => {
        const modifiedOptions = sourceDistrictOptions.map((opt) => ({
            ...opt,
            label: opt.districtNameEn,
            value: opt.districtId,
        })).sort((a, b) => a.label.localeCompare(b.label));
        return modifiedOptions;
    }, [sourceDistrictOptions]);

    const subDistrictOptionsTh = useMemo(() => {
        const modifiedOptions = sourceSubDistrictOptions.map((opt) => ({
            ...opt,
            label: opt.subDistrictName,
            value: opt.subDistrictId,
        })).sort((a, b) => a.label.localeCompare(b.label));
        return modifiedOptions;
    }, [sourceSubDistrictOptions]);

    const subDistrictOptionsEn = useMemo(() => {
        const modifiedOptions = sourceSubDistrictOptions.map((opt) => ({
            ...opt,
            label: opt.subDistrictNameEn,
            value: opt.subDistrictId,
        })).sort((a, b) => a.label.localeCompare(b.label));
        return modifiedOptions;
    }, [sourceSubDistrictOptions]);

    const nextStep = () => {
        setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    const createCompany = useCallback(
        async (values) => {
            try {
                dispatch(setIsLoading(true));
                await service.createCompany(
                    toParams({
                        ...values,
                    })
                );
                navigate("/admin/response_company");
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, navigate]
    );

    const handleProviceChange = useCallback(
        (val) => {
            clearDistrictOptions();
            clearSubDistrictOptions();
            if (val) {
                getDistrictOptions({ provinceId: val });
            }
        },
        [clearDistrictOptions, clearSubDistrictOptions, getDistrictOptions]
    );

    const handleDistrictChange = useCallback(
        (val) => {
            clearSubDistrictOptions();
            if (val) {
                getSubDistrictOptions({ districtId: val });
            }
        },
        [clearSubDistrictOptions, getSubDistrictOptions]
    );

    const onCompanyInfoSubmit = (values = {}) => {
        setInfoValues((prev) => ({ ...prev, ...values }));
        nextStep();
    };

    const onCompanyAddressSubmit = (values) => {
        setAddressValues((prev) => ({ ...prev, ...values }));
        nextStep();
    };

    const getTaxCompanyOption = useCallback(debounce(
        async ({ searchtext = "" }) => {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    searchtext
                };
                const response = await service.getTaxCompany(values);

                setOptionSearchTax(response?.data?.data?.map(o => {
                    return {
                        ...o,
                        label: o?.fullNameTh,
                        value: o?.taxId,
                    }
                }))
            } catch (error) {

            } finally {
                dispatch(setIsLoading(false));
            }
        }, 1000), []);

    const onCompanyCreateSubmit = (values) => {
        Modal.confirm({
            title: t("dialog.confirmation.header"),
            content: (i18n.language === "en" ?
                <span>Are you sure confirm to create company ?</span>
                : <span>คุณแน่ใจที่จะยืนยันการสร้างบริษัทหรือไม่ ?</span>
            ),
            onOk() {
                const modifiedValues = {
                    ...values,
                };
                createCompany(modifiedValues);
            },
            okText: t("common.confirm").toUpperCase(),
            cancelText: t("common.cancel").toUpperCase(),
        });
    };


    useEffect(() => {
        getMstCompanyTypeOptions();
    }, [getMstCompanyTypeOptions]);

    useEffect(() => {
        getProvinceOptions();
    }, [getProvinceOptions]);

    useEffect(() => {
        return () => {
            setInfoValues({});
            setAddressValues({});
            setContactValues({});
        };
    }, []);

    return {
        isLoading,
        currentStep,
        infoValues,
        addressValues,
        contactValues,
        companyTypeOptions,
        provinceOptionsTh,
        provinceOptionsEn,
        districtOptionsTh,
        districtOptionsEn,
        subDistrictOptionsTh,
        subDistrictOptionsEn,
        getTaxCompanyOption,
        optionSearchTax,
        handleProviceChange,
        handleDistrictChange,
        onCompanyCreateSubmit
    }
}

export default useAdminCreateCompany;